import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  VStack,
  Heading,
  Text,
  Button,
  HStack,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Card,
  CardBody,
  Textarea,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const EmailApprovalPage = () => {
  const { token, action } = useParams(); // action: 'approve' or 'reject'
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [approval, setApproval] = useState(null);
  const [comments, setComments] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApprovalDetails();
  }, [token]);

  const fetchApprovalDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/offers/approvals/verify/${token}`
      );
      setApproval(response.data.data);
      setError(null);
    } catch (error) {
      setError(
        error.response?.data?.message || "Invalid or expired approval link"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (action === "reject" && !rejectionReason.trim()) {
      toast({
        title: "Rejection Reason Required",
        description: "Please provide a reason for rejection",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setProcessing(true);
    try {
      const endpoint =
        action === "approve"
          ? `/api/v1/offers/approvals/email-approve/${token}`
          : `/api/v1/offers/approvals/email-reject/${token}`;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        action === "reject" ? { rejectionReason, comments } : { comments }
      );

      toast({
        title: `Offer ${action === "approve" ? "Approved" : "Rejected"}`,
        description: response.data.message || "Action completed successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Redirect after short delay
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error) {
      toast({
        title: "Action Failed",
        description:
          error.response?.data?.message || `Failed to ${action} offer`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.md" py={10}>
        <Box textAlign="center">
          <Spinner size="xl" />
          <Text mt={4}>Loading approval details...</Text>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.md" py={10}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={10}>
      <Card>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <Box textAlign="center">
              <Heading size="lg" mb={2}>
                {action === "approve" ? "Approve" : "Reject"} Offer
              </Heading>
              <Text color="gray.600">
                Review the offer details before taking action
              </Text>
            </Box>

            <Divider />

            {/* Offer Details */}
            <Box>
              <Text fontWeight="bold" fontSize="lg" mb={3}>
                Offer Details
              </Text>
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text color="gray.600">Offer ID:</Text>
                  <Text fontWeight="semibold">
                    {approval?.offerId?.offerId}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Candidate:</Text>
                  <Text fontWeight="semibold">
                    {approval?.offerId?.candidateId?.fullName || "N/A"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Position:</Text>
                  <Text fontWeight="semibold">
                    {approval?.offerId?.positionTitle || "N/A"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">CTC:</Text>
                  <Text fontWeight="semibold" color="green.600">
                    ₹{approval?.offerId?.ctc?.toLocaleString() || "N/A"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Status:</Text>
                  <Badge colorScheme="yellow">
                    {approval?.approvalStatus || "Pending"}
                  </Badge>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Approval Info */}
            <Box>
              <Text fontWeight="bold" fontSize="lg" mb={3}>
                Approval Information
              </Text>
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text color="gray.600">Workflow Type:</Text>
                  <Badge colorScheme="purple">
                    {approval?.workflowType || "sequential"}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Current Stage:</Text>
                  <Text fontWeight="semibold">
                    {approval?.currentStage} / {approval?.totalStages}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Comments */}
            <FormControl>
              <FormLabel>Comments (Optional)</FormLabel>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any comments..."
                rows={3}
              />
            </FormControl>

            {/* Rejection Reason */}
            {action === "reject" && (
              <FormControl isRequired>
                <FormLabel>
                  Rejection Reason{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  rows={4}
                />
              </FormControl>
            )}

            <Alert
              status={action === "approve" ? "success" : "warning"}
              borderRadius="md"
            >
              <AlertIcon />
              <Box>
                <Text fontSize="sm">
                  {action === "approve"
                    ? "This action will approve the offer and move it to the next stage (if applicable)."
                    : "This action will reject the offer and it cannot be undone."}
                </Text>
              </Box>
            </Alert>

            {/* Action Buttons */}
            <HStack justify="center" spacing={4}>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/")}
                isDisabled={processing}
              >
                Cancel
              </Button>
              <Button
                size="lg"
                colorScheme={action === "approve" ? "green" : "red"}
                onClick={handleAction}
                isLoading={processing}
              >
                Confirm {action === "approve" ? "Approval" : "Rejection"}
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
};

export default EmailApprovalPage;
